"use client";

import React, { createContext, useContext, useReducer } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ConfirmDialogState } from "@/types";
import { AlertTriangle, Info, CheckCircle, XCircle } from 'lucide-react'; // Import relevant Lucid icons

type ConfirmDialogAction =
  | { type: "OPEN_DIALOG"; payLoad: Partial<ConfirmDialogState> }
  | { type: "CLOSE_DIALOG" };


// Initial State
const initialState: ConfirmDialogState = {
  open: false,
  title: "Are you sure?",
  description: "This action cannot be undone.",
  confirmText: "Confirm",
  cancelText: "Cancel",
  onConfirm: () => {},
  type: "warning", // Default to warning
};

// Reducer
function confirmDialogReducer(state: ConfirmDialogState, action: ConfirmDialogAction): ConfirmDialogState {
  switch (action.type) {
    case "OPEN_DIALOG":
      return { ...state, ...action.payLoad, open: true };
    case "CLOSE_DIALOG":
      return { ...state, open: false };
    default:
      return state;
  }
}

// Context
export const ConfirmDialogContext = createContext<{
  state: ConfirmDialogState;
  dispatch: React.Dispatch<ConfirmDialogAction>;
} | null>(null);

// Provider
export const ConfirmDialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(confirmDialogReducer, initialState);

  return (
    <ConfirmDialogContext.Provider value={{ state, dispatch }}>
      {children}
      <ConfirmDialog />
    </ConfirmDialogContext.Provider>
  );
};

// Dialog Component
const ConfirmDialog = () => {
  const context = useContext(ConfirmDialogContext);
  if (!context) return null;

  const { state, dispatch } = context;

  const handleClose = () => dispatch({ type: "CLOSE_DIALOG" });
  const handleConfirm = () => {
    state.onConfirm();
    handleClose();
  };

  // Icon selection based on type
  let Icon = AlertTriangle; // Default icon for 'warning'
  let iconColor = "text-yellow-500"; // Default color for 'warning'

  switch (state.type) {
    case "error":
      Icon = XCircle;
      iconColor = "text-red-500";
      break;
    case "info":
      Icon = Info;
      iconColor = "text-slate-500";
      break;
    case "warning":
    default:
      Icon = AlertTriangle;
      iconColor = "text-yellow-500";
      break;
  }

  return (
    <Dialog open={state.open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <div className="flex items-center space-x-2 sm:flex-row sm:space-x-2 sm:space-y-0 flex-col space-y-2">
            {/* Conditionally render the icon */}
            <Icon className={`h-6 w-6 ${iconColor}`} />
            <DialogTitle className="text-center sm:text-left">
              {/* Render title as HTML if it's passed as raw HTML */}
              {typeof state.title === "string" ? state.title : <span dangerouslySetInnerHTML={state.title} />}
            </DialogTitle>
          </div>
          <DialogDescription className="text-m text-gray-700">
            {/* Render description as HTML if it's passed as raw HTML */}
            {typeof state.description === "string" ? state.description : <span dangerouslySetInnerHTML={state.description} />}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col sm:flex-row sm:justify-end space-x-0 sm:space-x-2 sm:space-y-0 space-y-2">
          <Button variant="destructive" onClick={handleClose}>
            {state.cancelText}
          </Button>
          <Button variant="outline" onClick={handleConfirm}>
            {state.confirmText}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
